import { assertValidImageData } from './pixel-analyzer.js';
import { ANALYSIS_THRESHOLDS } from './thresholds.js';
import type { CheckerboardDetection, PixelColor, RgbaImageData } from './types.js';

export interface CheckerboardOptions {
  readonly edgeBandFraction?: number;
  readonly maximumSamples?: number;
  readonly minimumSamples?: number;
  readonly minimumPaletteCoverage?: number;
  readonly minimumAxisPeriodicity?: number;
  readonly minimumCombinedPeriodicity?: number;
  readonly minimumTileSize?: number;
  readonly maximumTileSize?: number;
}

interface PaletteBucket {
  count: number;
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

interface PaletteAnalysis {
  palette: [PixelColor, PixelColor] | null;
  coverage: number;
  balance: number;
  opaqueRatio: number;
  sampleCount: number;
  sampleStride: number;
  bandX: number;
  bandY: number;
}

interface PeriodicityAnalysis {
  tileSize: number | null;
  horizontal: number;
  vertical: number;
  combined: number;
}

export function detectBakedCheckerboard(
  image: RgbaImageData,
  options: CheckerboardOptions = {},
): CheckerboardDetection {
  assertValidImageData(image);
  const config = resolveOptions(options);
  const paletteAnalysis = analyzePalette(image, config);

  if (!paletteAnalysis.palette) {
    return emptyResult(paletteAnalysis);
  }

  const [first, second] = paletteAnalysis.palette;
  const paletteDistance = colorDistance(first, second);
  const plausibleColors = isPlausibleCheckerColor(first) && isPlausibleCheckerColor(second);
  const plausibleDistance =
    paletteDistance >= ANALYSIS_THRESHOLDS.checkerboard.minimumPaletteDistance &&
    paletteDistance <= ANALYSIS_THRESHOLDS.checkerboard.maximumPaletteDistance;
  const periodicity = findPeriodicity(image, paletteAnalysis, config);
  // Do not lower the evidence floor for tiny images: a few pixels can easily
  // resemble a checkerboard by coincidence.
  const enoughSamples = paletteAnalysis.sampleCount >= config.minimumSamples;
  const coveragePass = paletteAnalysis.coverage >= config.minimumPaletteCoverage;
  const balancePass =
    paletteAnalysis.balance >= ANALYSIS_THRESHOLDS.checkerboard.minimumPaletteBalance;
  const opaquePass = paletteAnalysis.opaqueRatio >= ANALYSIS_THRESHOLDS.checkerboard.opaqueRatio;
  const periodicityPass =
    periodicity.tileSize !== null &&
    periodicity.horizontal >= config.minimumAxisPeriodicity &&
    periodicity.vertical >= config.minimumAxisPeriodicity &&
    periodicity.combined >= config.minimumCombinedPeriodicity;
  const suspected =
    enoughSamples &&
    plausibleColors &&
    plausibleDistance &&
    coveragePass &&
    balancePass &&
    opaquePass &&
    periodicityPass;
  const confidence = confidenceScore(
    paletteAnalysis.coverage,
    paletteAnalysis.balance,
    paletteAnalysis.opaqueRatio,
    periodicity.combined,
    enoughSamples && plausibleColors && plausibleDistance,
  );

  return {
    kind: 'baked-checkerboard',
    suspected,
    confidence,
    reasons: suspected
      ? [
          `Two opaque neutral or light colors cover ${Math.round(
            paletteAnalysis.coverage * 100,
          )}% of sampled edge pixels.`,
          `A ${periodicity.tileSize ?? 0}px alternating pattern repeats horizontally (${Math.round(
            periodicity.horizontal * 100,
          )}%) and vertically (${Math.round(periodicity.vertical * 100)}%).`,
          'This is a conservative heuristic; verify the image visually.',
        ]
      : [],
    palette: paletteAnalysis.palette,
    paletteCoverage: paletteAnalysis.coverage,
    opaqueRatio: paletteAnalysis.opaqueRatio,
    horizontalPeriodicity: periodicity.horizontal,
    verticalPeriodicity: periodicity.vertical,
    tileSize: periodicity.tileSize,
    sampleCount: paletteAnalysis.sampleCount,
  };
}

export const detectSuspectedBakedCheckerboard = detectBakedCheckerboard;

function analyzePalette(
  image: RgbaImageData,
  config: Required<CheckerboardOptions>,
): PaletteAnalysis {
  const bandX = edgeBandSize(image.width, config.edgeBandFraction);
  const bandY = edgeBandSize(image.height, config.edgeBandFraction);
  const interiorWidth = Math.max(0, image.width - bandX * 2);
  const interiorHeight = Math.max(0, image.height - bandY * 2);
  const approximateArea = image.width * image.height - interiorWidth * interiorHeight;
  const sampleStride = Math.max(1, Math.ceil(Math.sqrt(approximateArea / config.maximumSamples)));
  const buckets = new Map<string, PaletteBucket>();
  let sampleCount = 0;
  let opaqueCount = 0;

  for (let y = 0; y < image.height; y += sampleStride) {
    for (let x = 0; x < image.width; x += sampleStride) {
      if (!isEdgeRegion(x, y, image.width, image.height, bandX, bandY)) continue;
      const offset = (y * image.width + x) * 4;
      const r = image.data[offset] ?? 0;
      const g = image.data[offset + 1] ?? 0;
      const b = image.data[offset + 2] ?? 0;
      const a = image.data[offset + 3] ?? 0;
      const step = ANALYSIS_THRESHOLDS.checkerboard.quantizationStep;
      const key = `${quantize(r, step)},${quantize(g, step)},${quantize(b, step)}`;
      const bucket = buckets.get(key);
      sampleCount += 1;
      if (a >= ANALYSIS_THRESHOLDS.checkerboard.nearOpaqueAlpha) opaqueCount += 1;

      if (bucket) {
        bucket.count += 1;
        bucket.red += r;
        bucket.green += g;
        bucket.blue += b;
        bucket.alpha += a;
      } else {
        buckets.set(key, { count: 1, red: r, green: g, blue: b, alpha: a });
      }
    }
  }

  const sorted = [...buckets.values()].sort((left, right) => right.count - left.count);
  if (sorted.length < 2 || !sorted[0] || !sorted[1] || sampleCount === 0) {
    return {
      palette: null,
      coverage: sorted[0] ? sorted[0].count / Math.max(1, sampleCount) : 0,
      balance: 0,
      opaqueRatio: sampleCount ? opaqueCount / sampleCount : 0,
      sampleCount,
      sampleStride,
      bandX,
      bandY,
    };
  }

  const first = bucketColor(sorted[0]);
  const second = bucketColor(sorted[1]);
  const paletteCount = sorted[0].count + sorted[1].count;
  return {
    palette: [first, second],
    coverage: paletteCount / sampleCount,
    balance: Math.min(sorted[0].count, sorted[1].count) / paletteCount,
    opaqueRatio: opaqueCount / sampleCount,
    sampleCount,
    sampleStride,
    bandX,
    bandY,
  };
}

function findPeriodicity(
  image: RgbaImageData,
  paletteAnalysis: PaletteAnalysis,
  config: Required<CheckerboardOptions>,
): PeriodicityAnalysis {
  if (!paletteAnalysis.palette) return { tileSize: null, horizontal: 0, vertical: 0, combined: 0 };
  const maximumTile = Math.min(
    config.maximumTileSize,
    Math.floor(image.width / 2),
    Math.floor(image.height / 2),
  );
  if (maximumTile < config.minimumTileSize) {
    return { tileSize: null, horizontal: 0, vertical: 0, combined: 0 };
  }

  let best: PeriodicityAnalysis = { tileSize: null, horizontal: 0, vertical: 0, combined: 0 };
  for (let tile = config.minimumTileSize; tile <= maximumTile; tile += 1) {
    const horizontal = horizontalPeriodicity(image, paletteAnalysis, tile);
    const vertical = verticalPeriodicity(image, paletteAnalysis, tile);
    if (
      horizontal.comparisons < ANALYSIS_THRESHOLDS.checkerboard.minimumAxisComparisons ||
      vertical.comparisons < ANALYSIS_THRESHOLDS.checkerboard.minimumAxisComparisons
    ) {
      continue;
    }
    const combined = (horizontal.score + vertical.score) / 2;
    if (combined > best.combined) {
      best = {
        tileSize: tile,
        horizontal: horizontal.score,
        vertical: vertical.score,
        combined,
      };
    }
  }
  return best;
}

function horizontalPeriodicity(
  image: RgbaImageData,
  analysis: PaletteAnalysis,
  tile: number,
): { score: number; comparisons: number } {
  const lineStep = analysis.sampleStride;
  let successes = 0;
  let comparisons = 0;

  for (let y = 0; y < image.height; y += lineStep) {
    if (y >= analysis.bandY && y < image.height - analysis.bandY) continue;
    for (let x = 0; x + tile < image.width; x += lineStep) {
      const origin = paletteIndex(image, x, y, analysis.palette);
      if (origin < 0) continue;
      const opposite = paletteIndex(image, x + tile, y, analysis.palette);
      if (opposite >= 0) {
        comparisons += 1;
        if (opposite !== origin) successes += 1;
      }
      if (x + tile * 2 < image.width) {
        const repeated = paletteIndex(image, x + tile * 2, y, analysis.palette);
        if (repeated >= 0) {
          comparisons += 1;
          if (repeated === origin) successes += 1;
        }
      }
    }
  }
  return { score: comparisons ? successes / comparisons : 0, comparisons };
}

function verticalPeriodicity(
  image: RgbaImageData,
  analysis: PaletteAnalysis,
  tile: number,
): { score: number; comparisons: number } {
  const lineStep = analysis.sampleStride;
  let successes = 0;
  let comparisons = 0;

  for (let x = 0; x < image.width; x += lineStep) {
    if (x >= analysis.bandX && x < image.width - analysis.bandX) continue;
    for (let y = 0; y + tile < image.height; y += lineStep) {
      const origin = paletteIndex(image, x, y, analysis.palette);
      if (origin < 0) continue;
      const opposite = paletteIndex(image, x, y + tile, analysis.palette);
      if (opposite >= 0) {
        comparisons += 1;
        if (opposite !== origin) successes += 1;
      }
      if (y + tile * 2 < image.height) {
        const repeated = paletteIndex(image, x, y + tile * 2, analysis.palette);
        if (repeated >= 0) {
          comparisons += 1;
          if (repeated === origin) successes += 1;
        }
      }
    }
  }
  return { score: comparisons ? successes / comparisons : 0, comparisons };
}

function paletteIndex(
  image: RgbaImageData,
  x: number,
  y: number,
  palette: readonly [PixelColor, PixelColor] | null,
): number {
  if (!palette) return -1;
  const offset = (y * image.width + x) * 4;
  const alpha = image.data[offset + 3] ?? 0;
  if (alpha < ANALYSIS_THRESHOLDS.checkerboard.nearOpaqueAlpha) return -1;
  const color: PixelColor = {
    r: image.data[offset] ?? 0,
    g: image.data[offset + 1] ?? 0,
    b: image.data[offset + 2] ?? 0,
    a: alpha,
  };
  const firstDistance = colorDistance(color, palette[0]);
  const secondDistance = colorDistance(color, palette[1]);
  const bestDistance = Math.min(firstDistance, secondDistance);
  if (bestDistance > ANALYSIS_THRESHOLDS.checkerboard.assignmentDistance) return -1;
  return firstDistance <= secondDistance ? 0 : 1;
}

function emptyResult(analysis: PaletteAnalysis): CheckerboardDetection {
  return {
    kind: 'baked-checkerboard',
    suspected: false,
    confidence: 0,
    reasons: [],
    palette: null,
    paletteCoverage: analysis.coverage,
    opaqueRatio: analysis.opaqueRatio,
    horizontalPeriodicity: 0,
    verticalPeriodicity: 0,
    tileSize: null,
    sampleCount: analysis.sampleCount,
  };
}

function resolveOptions(options: CheckerboardOptions): Required<CheckerboardOptions> {
  const defaults = ANALYSIS_THRESHOLDS.checkerboard;
  const resolved = {
    edgeBandFraction: options.edgeBandFraction ?? defaults.edgeBandFraction,
    maximumSamples: options.maximumSamples ?? defaults.maximumSamples,
    minimumSamples: options.minimumSamples ?? defaults.minimumSamples,
    minimumPaletteCoverage: options.minimumPaletteCoverage ?? defaults.minimumPaletteCoverage,
    minimumAxisPeriodicity: options.minimumAxisPeriodicity ?? defaults.minimumAxisPeriodicity,
    minimumCombinedPeriodicity:
      options.minimumCombinedPeriodicity ?? defaults.minimumCombinedPeriodicity,
    minimumTileSize: options.minimumTileSize ?? defaults.minimumTileSize,
    maximumTileSize: options.maximumTileSize ?? defaults.maximumTileSize,
  };
  assertRatio(resolved.edgeBandFraction, 'edgeBandFraction', false);
  assertPositiveInteger(resolved.maximumSamples, 'maximumSamples');
  assertPositiveInteger(resolved.minimumSamples, 'minimumSamples');
  assertRatio(resolved.minimumPaletteCoverage, 'minimumPaletteCoverage');
  assertRatio(resolved.minimumAxisPeriodicity, 'minimumAxisPeriodicity');
  assertRatio(resolved.minimumCombinedPeriodicity, 'minimumCombinedPeriodicity');
  assertPositiveInteger(resolved.minimumTileSize, 'minimumTileSize');
  assertPositiveInteger(resolved.maximumTileSize, 'maximumTileSize');
  if (resolved.maximumTileSize < resolved.minimumTileSize) {
    throw new RangeError('maximumTileSize must be at least minimumTileSize.');
  }
  return resolved;
}

function edgeBandSize(dimension: number, fraction: number): number {
  return Math.min(
    Math.ceil(dimension / 2),
    ANALYSIS_THRESHOLDS.checkerboard.maximumEdgeBandPixels,
    Math.max(
      ANALYSIS_THRESHOLDS.checkerboard.minimumEdgeBandPixels,
      Math.ceil(dimension * fraction),
    ),
  );
}

function isEdgeRegion(
  x: number,
  y: number,
  width: number,
  height: number,
  bandX: number,
  bandY: number,
): boolean {
  return x < bandX || x >= width - bandX || y < bandY || y >= height - bandY;
}

function bucketColor(bucket: PaletteBucket): PixelColor {
  return {
    r: Math.round(bucket.red / bucket.count),
    g: Math.round(bucket.green / bucket.count),
    b: Math.round(bucket.blue / bucket.count),
    a: Math.round(bucket.alpha / bucket.count),
  };
}

function isPlausibleCheckerColor(color: PixelColor): boolean {
  const maximum = Math.max(color.r, color.g, color.b);
  const minimum = Math.min(color.r, color.g, color.b);
  const neutral = maximum - minimum <= ANALYSIS_THRESHOLDS.checkerboard.neutralChannelSpread;
  const light = minimum >= ANALYSIS_THRESHOLDS.checkerboard.lightColorMinimum;
  return neutral || light;
}

function colorDistance(first: PixelColor, second: PixelColor): number {
  return Math.hypot(first.r - second.r, first.g - second.g, first.b - second.b);
}

function confidenceScore(
  coverage: number,
  balance: number,
  opaqueRatio: number,
  periodicity: number,
  plausible: boolean,
): number {
  if (!plausible) return 0;
  const normalizedBalance = Math.min(1, balance / 0.5);
  return Math.max(
    0,
    Math.min(
      100,
      Math.round(
        (coverage * 0.25 + normalizedBalance * 0.1 + opaqueRatio * 0.15 + periodicity * 0.5) * 100,
      ),
    ),
  );
}

function quantize(channel: number, step: number): number {
  return Math.min(255, Math.round(channel / step) * step);
}

function assertPositiveInteger(value: number, name: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer.`);
  }
}

function assertRatio(value: number, name: string, allowZero = true): void {
  if (!Number.isFinite(value) || value > 1 || (allowZero ? value < 0 : value <= 0)) {
    throw new RangeError(
      `${name} must be ${allowZero ? 'between 0 and 1' : 'greater than 0 and at most 1'}.`,
    );
  }
}
